import axios, { AxiosError } from 'axios'
import { attachAuthInterceptors } from './client'
import type { Eligibility, Participation, ProgramInfo } from '@/types/programs'

/**
 * The programs API, reached same-origin through /programs-api (server.mjs and
 * the Vite dev proxy add the brand key). It accepts the Companion session, so
 * it shares the session handling of the Companion client.
 */
const programsClient = attachAuthInterceptors(
  axios.create({ baseURL: '/programs-api', headers: { 'Content-Type': 'application/json' } })
)

// A host without the programs proxy serves the SPA's index.html (status 200) instead.
programsClient.interceptors.response.use((response) => {
  const contentType = String(response.headers['content-type'] ?? '')
  if (contentType.includes('text/html')) {
    return Promise.reject(
      new AxiosError('Programs API unavailable', AxiosError.ERR_BAD_RESPONSE, response.config, response.request, response)
    )
  }
  return response
})

export async function getProgram(slug: string, signal?: AbortSignal): Promise<ProgramInfo> {
  const res = await programsClient.get<ProgramInfo>(`/programs/${encodeURIComponent(slug)}`, { signal })
  return res.data
}

export async function getEligibility(
  slug: string,
  addressUuid: string,
  signal?: AbortSignal
): Promise<Eligibility> {
  const res = await programsClient.get<Eligibility>(
    `/programs/${encodeURIComponent(slug)}/eligibility`,
    { params: { addressUuid }, signal }
  )
  return res.data
}

export async function enrol(
  slug: string,
  input: { addressUuid: string; tcProgramVersion: string; tcDataSharingVersion: string }
): Promise<Participation> {
  const res = await programsClient.post<Participation>(
    `/programs/${encodeURIComponent(slug)}/participations`,
    { ...input, acceptProgramTerms: true, acceptDataSharingTerms: true }
  )
  return res.data
}

export async function joinWaitlist(slug: string, addressUuid?: string): Promise<void> {
  await programsClient.post(`/programs/${encodeURIComponent(slug)}/waitlist`, { addressUuid })
}

/** `withDevices: false` when only statuses and dates are needed: much faster, device health 'unknown'. */
export async function listParticipations(
  signal?: AbortSignal,
  options: { withDevices?: boolean } = {}
): Promise<Participation[]> {
  const res = await programsClient.get<{ results: Participation[] }>('/participations', {
    signal,
    params: options.withDevices === false ? { devices: 'false' } : undefined,
  })
  return res.data.results
}

export async function optOut(participationId: string): Promise<Participation> {
  const res = await programsClient.post<Participation>(
    `/participations/${encodeURIComponent(participationId)}/opt-out`
  )
  return res.data
}

export async function undoOptOut(participationId: string): Promise<Participation> {
  const res = await programsClient.post<Participation>(
    `/participations/${encodeURIComponent(participationId)}/undo-opt-out`
  )
  return res.data
}

/** The API's error details (`{ error: { details } }`), if any. */
export function programsErrorDetails(err: unknown): Record<string, unknown> {
  if (!axios.isAxiosError(err)) return {}
  const details = (err.response?.data as { error?: { details?: unknown } } | undefined)?.error?.details
  return details && typeof details === 'object' ? (details as Record<string, unknown>) : {}
}

/** The API's error code (`{ error: { code } }`), or null for network trouble and the like. */
export function programsErrorCode(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null
  const code = (err.response?.data as { error?: { code?: unknown } } | undefined)?.error?.code
  return typeof code === 'string' ? code : null
}
